import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import type { InvoiceSummary, TicketDetail as Detail } from '@aviaconsole/shared';
import { TICKET_PRIORITIES, TICKET_STATUSES } from '@aviaconsole/shared';
import { capi, companyUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate, slaLabel } from '../lib/format';
import { useToast } from '../components/Toast';
import { BackLink, Button, Card, Field, Loading, PageHeader, Select, cap, ticketStatusLabel } from '../components/ui';

export function TicketDetail() {
  const { id } = useParams();
  const { company, user } = useAuth();
  const { t, s, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['ticket', company?.id, id];
  const q = useQuery({ queryKey: key, queryFn: () => capi<Detail>(`/tickets/${id}`) });
  const invoices = useQuery({ queryKey: ['invoices', company?.id, 'all', ''], queryFn: () => capi<{ items: InvoiceSummary[] }>('/invoices?limit=200') });
  const [body, setBody] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const tk = q.data;

  const post = useMutation({
    mutationFn: (internal: boolean) => capi<Detail>(`/tickets/${id}/messages`, { method: 'POST', json: { body, internal } }),
    onSuccess: (d) => { qc.setQueryData(key, d); qc.invalidateQueries({ queryKey: ['tickets'] }); setBody(''); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const patch = useMutation({
    mutationFn: (json: Record<string, unknown>) => capi<Detail>(`/tickets/${id}`, { method: 'PATCH', json }),
    onSuccess: (d) => { qc.setQueryData(key, d); qc.invalidateQueries({ queryKey: ['tickets'] }); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => { const fd = new FormData(); fd.append('file', file); return capi<Detail>(`/tickets/${id}/attachments`, { method: 'POST', body: fd }); },
    onSuccess: (d) => { qc.setQueryData(key, d); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (!tk) return <Loading />;
  const sla = slaLabel(tk.firstResponseDueAt, tk.firstRespondedAt, tk.status, lang);
  return (
    <>
      <PageHeader crumb={t('tckDetailCrumb')} title={tk.number} sub={`${tk.customerName ?? '—'} · ${s(cap(tk.priority))} · ${sla.label}`} />
      <div className="grid two" style={{ gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)' }}>
        <Card>
          <div style={{ marginBottom: '0.9rem' }}><BackLink to="/tickets" label={t('backToTickets')} /></div>
          <div style={{ fontWeight: 700, marginBottom: '0.7rem' }}>{tk.subject}</div>
          <div className="stack md">
            {tk.messages.map((m) => (
              <div key={m.id} className={`msg ${m.internal ? 'internal' : ''}`}>
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>{m.authorName}{m.internal ? ` · ${t('internalNote').toLowerCase()}` : ''}</span>
                  <span className="mono small muted">{formatDate(m.createdAt, lang, true)}</span>
                </div>
                <p>{m.body}</p>
              </div>
            ))}
          </div>
          <div className="stack" style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid var(--glass-border)', gap: '0.6rem' }}>
            <textarea className="textbox" placeholder={t('writeReply')} value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 88, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
            <div className="row">
              <Button variant="primary" icon="fas fa-paper-plane" disabled={!body.trim() || post.isPending} onClick={() => post.mutate(false)}>{t('sendReply')}</Button>
              <Button icon="fas fa-note-sticky" disabled={!body.trim() || post.isPending} onClick={() => post.mutate(true)}>{t('internalNote')}</Button>
              <Button icon="fas fa-paperclip" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>{t('attach')}</Button>
              <input ref={fileRef} type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
            </div>
          </div>
        </Card>
        <div className="stack">
          <Card>
            <h2>{t('hProperties')}</h2>
            <div className="stack md">
              <Field label={t('customerLabel')} value={tk.customerId ? <a href={`/customers/${tk.customerId}`}>{tk.customerName}</a> : '—'} />
              <Select label={t('priority')} value={tk.priority} options={TICKET_PRIORITIES.map((p) => ({ value: p, label: s(cap(p)) }))} onChange={(e) => patch.mutate({ priority: e.target.value })} />
              <Select label={t('status')} value={tk.status} options={TICKET_STATUSES.map((st) => ({ value: st, label: s(ticketStatusLabel(st)) }))} onChange={(e) => patch.mutate({ status: e.target.value })} />
              <Field label={t('assignee')} value={<div className="row">{tk.assigneeName ?? s('Unassigned')}{tk.assigneeUserId !== user?.id && <button type="button" className="linkbtn" onClick={() => patch.mutate({ assigneeUserId: user?.id })}>→ {user?.name}</button>}</div>} />
              <Field label={t('firstResponse')} value={tk.firstResponseDueAt ? `${formatDate(tk.firstResponseDueAt, lang, true)} · ${sla.label}` : '—'} />
              <Select label={t('linkedInvoice')} value={tk.linkedInvoiceId ?? ''} options={[{ value: '', label: '—' }, ...(invoices.data?.items ?? []).filter((i) => i.status !== 'draft').map((i) => ({ value: i.id, label: `${i.number} · ${i.customerName}` }))]} onChange={(e) => patch.mutate({ linkedInvoiceId: e.target.value || null })} />
              {tk.linkedInvoiceId && <a className="linkbtn" href={`/invoices/${tk.linkedInvoiceId}`}>{tk.linkedInvoiceNumber} <i className="fas fa-arrow-up-right-from-square" style={{ fontSize: '0.7rem' }} /></a>}
            </div>
          </Card>
          <Card>
            <h2 style={{ marginBottom: '0.7rem' }}>{t('hAttachments')}</h2>
            <div className="stack sm">
              {tk.attachments.length === 0 && <div className="small muted">{t('nothingHere')}</div>}
              {tk.attachments.map((a) => (
                <a key={a.id} className="subcard row" style={{ flexWrap: 'nowrap', color: 'inherit' }} href={companyUrl(`/tickets/${id}/attachments/${a.id}`)} target="_blank" rel="noreferrer">
                  <i className={a.contentType.includes('pdf') ? 'fas fa-file-pdf' : a.contentType.startsWith('image/') ? 'fas fa-file-image' : 'fas fa-file'} aria-hidden="true" style={{ color: 'var(--accent)' }} />
                  <span style={{ flex: 1, fontSize: '0.82rem' }}>{a.filename}</span>
                  <span className="small muted">{Math.round(a.size / 1024)} KB</span>
                </a>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
